import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { DollarSign, Home, TrendingUp, Users, Award } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminReports = () => {
  const [listingSummary, setListingSummary] = useState(null);
  const [employeePerformance, setEmployeePerformance] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchReports();
  }, []);

  const fetchReports = async () => {
    try {
      const [summaryRes, performanceRes] = await Promise.all([
        axios.get(`${API}/reports/listings-summary`),
        axios.get(`${API}/reports/employee-performance`)
      ]);
      setListingSummary(summaryRes.data);
      setEmployeePerformance(performanceRes.data.employees);
    } catch (error) {
      console.error('Failed to fetch reports:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0
    }).format(price);
  };

  const COLORS = ['hsl(354, 70%, 54%)', '#000000', '#666666', '#999999', '#cccccc'];

  const statusData = listingSummary ? [
    { name: 'Available', value: listingSummary.by_status?.available || 0 },
    { name: 'Pending', value: listingSummary.by_status?.pending || 0 },
    { name: 'Sold', value: listingSummary.by_status?.sold || 0 }
  ] : [];

  const priceData = listingSummary?.price_distribution || [];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <h1 className="font-heading text-2xl font-bold tracking-tight text-foreground">
          Reports & Analytics
        </h1>
        <p className="font-body text-muted-foreground mt-1">
          View performance metrics and insights
        </p>
      </div>

      <Tabs defaultValue="listings" className="space-y-6">
        <TabsList className="bg-secondary rounded-none p-1">
          <TabsTrigger value="listings" className="rounded-none font-heading uppercase tracking-wide text-xs" data-testid="tab-listings">
            Listings Report
          </TabsTrigger>
          <TabsTrigger value="employees" className="rounded-none font-heading uppercase tracking-wide text-xs" data-testid="tab-employees">
            Employee Performance
          </TabsTrigger>
        </TabsList>

        <TabsContent value="listings" className="space-y-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <Card className="rounded-none border-border">
              <CardHeader className="pb-2">
                <CardTitle className="font-heading text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-2">
                  <Home className="w-4 h-4" />
                  Total Listings
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="font-heading text-3xl font-bold" data-testid="report-total-listings">
                  {listingSummary?.total_listings || 0}
                </p>
              </CardContent>
            </Card>
            <Card className="rounded-none border-border">
              <CardHeader className="pb-2">
                <CardTitle className="font-heading text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-2">
                  <DollarSign className="w-4 h-4" />
                  Total Value
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="font-heading text-3xl font-bold text-primary" data-testid="report-total-value">
                  {formatPrice(listingSummary?.total_value || 0)}
                </p>
              </CardContent>
            </Card>
            <Card className="rounded-none border-border">
              <CardHeader className="pb-2">
                <CardTitle className="font-heading text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-2">
                  <TrendingUp className="w-4 h-4" />
                  Sold Value
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="font-heading text-3xl font-bold text-primary" data-testid="report-sold-value">
                  {formatPrice(listingSummary?.value_by_status?.sold || 0)}
                </p>
              </CardContent>
            </Card>
            <Card className="rounded-none border-border">
              <CardHeader className="pb-2">
                <CardTitle className="font-heading text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-2">
                  <Award className="w-4 h-4" />
                  Sold Count
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="font-heading text-3xl font-bold" data-testid="report-sold-count">
                  {listingSummary?.by_status?.sold || 0}
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="rounded-none border-border">
              <CardHeader>
                <CardTitle className="font-heading text-lg font-semibold">Listings by Status</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={statusData}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={100}
                        paddingAngle={2}
                        dataKey="value"
                        label={({ name, value }) => `${name}: ${value}`}
                      >
                        {statusData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            <Card className="rounded-none border-border">
              <CardHeader>
                <CardTitle className="font-heading text-lg font-semibold">Price Distribution</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={priceData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" />
                      <XAxis dataKey="label" tick={{ fontSize: 10 }} />
                      <YAxis tick={{ fontSize: 12 }} />
                      <Tooltip />
                      <Bar dataKey="count" fill="hsl(354, 70%, 54%)" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* City Breakdown */}
          {listingSummary?.by_city && Object.keys(listingSummary.by_city).length > 0 && (
            <Card className="rounded-none border-border">
              <CardHeader>
                <CardTitle className="font-heading text-lg font-semibold">Listings by City</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                  {Object.entries(listingSummary.by_city).map(([city, count]) => (
                    <div key={city} className="bg-secondary/50 p-4">
                      <p className="font-body text-sm text-muted-foreground">{city}</p>
                      <p className="font-heading text-2xl font-bold">{count}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="employees" className="space-y-6">
          <Card className="rounded-none border-border">
            <CardHeader>
              <CardTitle className="font-heading text-lg font-semibold flex items-center gap-2">
                <Users className="w-5 h-5" />
                Employee Performance Overview
              </CardTitle>
            </CardHeader>
            <CardContent>
              {employeePerformance.length === 0 ? (
                <div className="text-center py-12">
                  <Users className="w-12 h-12 text-muted-foreground mx-auto mb-2" />
                  <p className="font-body text-muted-foreground">No employee data available</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="bg-secondary/50">
                      <TableHead className="font-heading text-xs uppercase tracking-widest">Rank</TableHead>
                      <TableHead className="font-heading text-xs uppercase tracking-widest">Employee</TableHead>
                      <TableHead className="font-heading text-xs uppercase tracking-widest text-center">Assigned</TableHead>
                      <TableHead className="font-heading text-xs uppercase tracking-widest text-center">Available</TableHead>
                      <TableHead className="font-heading text-xs uppercase tracking-widest text-center">Pending</TableHead>
                      <TableHead className="font-heading text-xs uppercase tracking-widest text-center">Sold</TableHead>
                      <TableHead className="font-heading text-xs uppercase tracking-widest text-right">Total Sold Value</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {employeePerformance.map((emp, index) => (
                      <TableRow key={emp.employee_id} className="hover:bg-secondary/30" data-testid={`performance-row-${emp.employee_id}`}>
                        <TableCell>
                          <div className={`w-8 h-8 flex items-center justify-center font-heading font-bold ${
                            index === 0 ? 'bg-primary text-white' : 'bg-black text-white'
                          }`}>
                            {index + 1}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div>
                            <p className="font-heading font-semibold text-sm">{emp.employee_name}</p>
                            <p className="font-body text-xs text-muted-foreground">{emp.employee_email}</p>
                          </div>
                        </TableCell>
                        <TableCell className="text-center font-heading font-semibold">
                          {emp.total_assigned}
                        </TableCell>
                        <TableCell className="text-center font-body">
                          {emp.available}
                        </TableCell>
                        <TableCell className="text-center font-body text-yellow-600">
                          {emp.pending}
                        </TableCell>
                        <TableCell className="text-center font-heading font-bold text-primary">
                          {emp.sold}
                        </TableCell>
                        <TableCell className="text-right font-heading font-bold text-primary">
                          {formatPrice(emp.total_sold_value)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>

          {/* Performance Chart */}
          {employeePerformance.length > 0 && (
            <Card className="rounded-none border-border">
              <CardHeader>
                <CardTitle className="font-heading text-lg font-semibold">Sales by Employee</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={employeePerformance} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" />
                      <XAxis type="number" tick={{ fontSize: 12 }} />
                      <YAxis dataKey="employee_name" type="category" tick={{ fontSize: 12 }} width={100} />
                      <Tooltip formatter={(value) => formatPrice(value)} />
                      <Bar dataKey="total_sold_value" fill="hsl(354, 70%, 54%)" name="Sold Value" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminReports;
